import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutlet } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { OfflineBanner } from '@/components/kiosk/offline-banner';
import { IdleOverlay } from '@/components/kiosk/idle-overlay';
import { ProgressDots } from '@/components/ui/progress-dots';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { useIdleTimer } from '@/hooks/use-idle-timer';
import { useSessionStore } from '@/stores/session-store';
import { resetSession } from '@/lib/session-reset';
import type { ScreenName } from '@/types/domain';

/**
 * Snapshots the outlet at mount so the exiting AnimatePresence copy keeps
 * rendering the old screen. A live <Outlet /> re-resolves to the *new* route
 * inside the exiting wrapper, mounting every screen twice per navigation
 * (which, on the confirmation screen, created a duplicate DRAFT order).
 */
function FrozenOutlet() {
  const outlet = useOutlet();
  const [frozen] = useState(outlet);
  return frozen;
}

function screenFromPath(pathname: string): ScreenName {
  if (pathname === '/') return 'welcome';
  if (pathname.startsWith('/order-type')) return 'order-type';
  if (pathname.startsWith('/menu')) return 'menu';
  if (pathname.startsWith('/customize')) return 'customization';
  if (pathname.startsWith('/cart')) return 'cart';
  if (pathname.startsWith('/confirm')) return 'confirmation';
  if (pathname.startsWith('/instructions')) return 'instructions';
  return 'welcome';
}

export function KioskShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const screen = screenFromPath(location.pathname);

  const { idleWarningVisible, setCurrentScreen, setIdleWarningVisible, requestCloseModals } =
    useSessionStore();

  useEffect(() => {
    setCurrentScreen(screen);
  }, [screen, setCurrentScreen]);

  // Idle is paused on welcome (the attract loop IS the idle state) and on the
  // instructions screen (it owns its own 30s auto-reset timer).
  const idleEnabled = screen !== 'welcome' && screen !== 'instructions';

  useIdleTimer({
    enabled: idleEnabled,
    onActivity: () => setIdleWarningVisible(false),
    onWarning: () => {
      // Close any open screen-level modal first — never stack overlays.
      requestCloseModals();
      setIdleWarningVisible(true);
    },
    onReset: () => {
      setIdleWarningVisible(false);
      void resetSession();
      navigate('/');
    },
  });

  return (
    <div className="flex h-full flex-col bg-cream">
      <OfflineBanner visible={!online} />

      <main className="relative flex-1 overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="absolute inset-0 flex flex-col overflow-y-auto"
          >
            <FrozenOutlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {screen !== 'welcome' && (
        <footer className="border-t border-hairline bg-surface py-4">
          <ProgressDots current={screen} />
        </footer>
      )}

      <IdleOverlay open={idleWarningVisible} onContinue={() => setIdleWarningVisible(false)} />
    </div>
  );
}
